import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";

import {
  fetchProductType,
  updateProductType,
  deleteProductType,
  addProductCriteria,
  deleteProductCriteria,
  addProductCriteriaValue,
  deleteProductCriteriaValue,
  fetchProductTasks,
  createProductTask,
  updateProductTask,
  deleteProductTask,
} from "../../api/companyApi";
import { COMPANY_ID } from "../../config/appConfig";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

const PRODUCT_CLASS_LABELS = {
  Goods: "productType.class.goods",
  PersonalService: "productType.class.personalService",
  ApplicationService: "productType.class.applicationService",
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function ProductTypeDetailPage() {
  const { businessDomainId, productTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // KPI state
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(-1);
  const [kpiForm, setKpiForm] = useState({
    indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "",
  });
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(-1);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // Criteria state
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({
    classificationCriteriaCd: "", classificationCriteriaName: "",
  });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // Criteria Value state
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({
    classificationCriteriaValueCd: "", classificationCriteriaValueName: "",
  });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  // Product Task state
  const [tasks, setTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskEditMode, setTaskEditMode] = useState(false);
  const [taskEditTarget, setTaskEditTarget] = useState(null);
  const [taskForm, setTaskForm] = useState({
    productTaskName: "", productTaskDescription: "", url: "",
  });
  const [taskConfirmOpen, setTaskConfirmOpen] = useState(false);
  const [taskDeleteTarget, setTaskDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try { setData(await fetchProductType(productTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  const loadTasks = useCallback(async () => {
    try { setTasks(await fetchProductTasks(productTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  useEffect(() => { loadData(); loadTasks(); }, [loadData, loadTasks]);

  // ── Delete product type ──
  const handleDeleteProductType = async () => {
    try {
      await deleteProductType(productTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/product-types`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── KPI CRUD ──
  const openKpiAddDialog = () => {
    setKpiEditMode(false); setKpiEditIndex(-1);
    setKpiForm({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
    setKpiDialogOpen(true);
  };
  const openKpiEditDialog = (index, indicator) => {
    setKpiEditMode(true); setKpiEditIndex(index);
    setKpiForm({
      indicatorName: indicator.indicatorName || "", unit: indicator.unit || "",
      measuringMethod: indicator.measuringMethod || "", evaluationStandard: indicator.evaluationStandard || "",
      description: indicator.description || "",
    });
    setKpiDialogOpen(true);
  };
  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName || !kpiForm.unit || !kpiForm.description) { notify.error(t("message.error.required")); return; }
    try {
      const indicators = [...(data.indicators || [])];
      if (kpiEditMode) {
        indicators[kpiEditIndex] = { ...indicators[kpiEditIndex], ...kpiForm, updatedAt: new Date().toISOString() };
      } else {
        if (indicators.some((ind) => ind.indicatorName === kpiForm.indicatorName)) { notify.error(t("productKpi.error.duplicate")); return; }
        indicators.push({ ...kpiForm, createdAt: new Date().toISOString() });
      }
      await updateProductType({ ...data, indicators });
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    try {
      const indicators = [...(data.indicators || [])];
      indicators.splice(kpiDeleteIndex, 1);
      await updateProductType({ ...data, indicators });
      notify.success(t("message.delete.success"));
      setKpiConfirmOpen(false); setKpiDeleteIndex(-1); loadData();
    } catch (e) { notify.apiError(e); }
  };

  // ── Criteria CRUD ──
  const openCriteriaAddDialog = () => {
    setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" });
    setCriteriaDialogOpen(true);
  };
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) { notify.error(t("message.error.required")); return; }
    try {
      await addProductCriteria(productTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success"));
      setCriteriaDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCriteriaDelete = async () => {
    try {
      await deleteProductCriteria(productTypeId, criteriaDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const openCvAddDialog = () => {
    setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
    setCvDialogOpen(true);
  };
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addProductCriteriaValue(productTypeId, {
        classificationCriteria: selectedCriteria,
        classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd,
        classificationCriteriaValueName: cvForm.classificationCriteriaValueName,
        createdValueAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      setCvDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCvDelete = async () => {
    try {
      await deleteProductCriteriaValue(productTypeId, cvDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  // ── Product Task CRUD ──
  const openTaskAddDialog = () => {
    setTaskEditMode(false); setTaskEditTarget(null);
    setTaskForm({ productTaskName: "", productTaskDescription: "", url: "" });
    setTaskDialogOpen(true);
  };
  const openTaskEditDialog = (task) => {
    setTaskEditMode(true); setTaskEditTarget(task);
    setTaskForm({
      productTaskName: task.productTaskName || "",
      productTaskDescription: task.productTaskDescription || "",
      url: task.url || "",
    });
    setTaskDialogOpen(true);
  };
  const handleTaskSave = async () => {
    if (!taskForm.productTaskName || !taskForm.productTaskDescription) { notify.error(t("message.error.required")); return; }
    try {
      if (taskEditMode && taskEditTarget) {
        await updateProductTask({
          ...taskEditTarget,
          ...taskForm,
          createdAt: taskEditTarget.createdAt,
        });
      } else {
        await createProductTask({
          companyId: COMPANY_ID,
          productTypeId,
          productTypeName: data.productTypeName,
          ...taskForm,
        });
      }
      notify.success(taskEditMode ? t("message.update.success") : t("message.create.success"));
      setTaskDialogOpen(false); loadTasks();
    } catch (e) { notify.apiError(e); }
  };
  const handleTaskDelete = async () => {
    try {
      await deleteProductTask(taskDeleteTarget.productTaskId);
      notify.success(t("message.delete.success"));
      loadTasks();
    } catch (e) { notify.apiError(e); }
    setTaskConfirmOpen(false); setTaskDeleteTarget(null);
  };

  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  if (!data) return null;

  const productClassLabel = PRODUCT_CLASS_LABELS[data.productClass] ? t(PRODUCT_CLASS_LABELS[data.productClass]) : data.productClass || "";
  const indicators = data.indicators || [];
  const criterias = data.classificationCriterias || [];
  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));

  // Filter criteria values for the selected criteria
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter(
        (v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName
      )
    : [];

  const kpiColumns = [
    { field: "indicatorName", headerName: t("productKpi.indicatorName"), flex: 1, minWidth: 150,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {p.row.indicatorName}
            {isLocked && <Chip icon={<LockIcon fontSize="small" />} label={t("common.inUse")} size="small" color="warning" variant="outlined" />}
          </Box>
        );
      },
    },
    { field: "unit", headerName: t("productKpi.unit"), width: 120 },
    { field: "description", headerName: t("productKpi.description"), flex: 1, minWidth: 150, renderCell: wrapTextCell },
    { field: "actions", headerName: t("common.actions"), width: 140, sortable: false,
      renderCell: (p) => {
        const idx = indicators.findIndex((ind) => ind.indicatorName === p.row.indicatorName);
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (<Box>
          <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}><span>
            <IconButton size="small" disabled={isLocked} onClick={(e) => { e.stopPropagation(); openKpiEditDialog(idx, p.row); }}><EditIcon fontSize="small" /></IconButton>
          </span></Tooltip>
          <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}><span>
            <IconButton size="small" color="error" disabled={isLocked} onClick={(e) => { e.stopPropagation(); setKpiDeleteIndex(idx); setKpiConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
          </span></Tooltip>
        </Box>);
      },
    },
  ];
  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("productCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("productCriteria.name"), flex: 1, minWidth: 200 },
    { field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setCriteriaDeleteTarget({ classificationCriteriaCd: p.row.classificationCriteriaCd, classificationCriteriaName: p.row.classificationCriteriaName });
          setCriteriaConfirmOpen(true);
        }}><DeleteIcon fontSize="small" /></IconButton>
      ),
    },
  ];
  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    { field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCvDeleteTarget(p.row); setCvConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
  const taskColumns = [
    { field: "productTaskName", headerName: t("productTask.name"), flex: 1, minWidth: 200 },
    { field: "productTaskDescription", headerName: t("productTask.description"), flex: 1, minWidth: 200, renderCell: wrapTextCell },
    { field: "url", headerName: t("productTask.url"), flex: 1, minWidth: 150 },
    { field: "actions", headerName: t("common.actions"), width: 120, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openTaskEditDialog(p.row); }}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setTaskDeleteTarget(p.row); setTaskConfirmOpen(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("productType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/design/${businessDomainId}/product-types/${productTypeId}/edit`)}>{t("common.edit")}</Button>
          <Tooltip title={hasLockedKpi ? t("kpi.constraint.type.locked") : ""}><span>
            <Button variant="contained" color="error" startIcon={<DeleteIcon />} disabled={hasLockedKpi} onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
          </span></Tooltip>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("productType.name")} value={data.productTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("productType.productClass")} value={productClassLabel} /></Grid>
          <Grid size={12}><DetailField label={t("productType.valueProposition")} value={data.valueProposition} /></Grid>
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("productKpi.title")} />
          <Tab label={t("productCriteria.title")} />
          <Tab label={t("productTask.title")} />
        </Tabs>
      </Box>

      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("productKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAddDialog}>{t("common.add")}</Button>
        </Box>
        <DataGrid rows={indicators} columns={kpiColumns} getRowId={(r) => r.indicatorName}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick getRowHeight={() => 'auto'}
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }} />
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        {selectedCriteria ? (
          /* ── Criteria Value view ── */
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1">
                {selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCvAddDialog}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid
              rows={criteriaValues}
              columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          /* ── Criteria list view ── */
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("productCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCriteriaAddDialog}>{t("common.add")}</Button>
            </Box>
            <DataGrid rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("productTask.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openTaskAddDialog}>{t("common.add")}</Button>
        </Box>
        <DataGrid rows={tasks} columns={taskColumns} getRowId={(r) => r.productTaskId}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick getRowHeight={() => 'auto'} />
      </TabPanel>

      <Box sx={{ mt: 3 }}><Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/product-types`)}>{t("common.back")}</Button></Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteProductType} onCancel={() => setConfirmOpen(false)} />

      {/* KPI Detail Dialog */}
      <KpiDetailDialog open={kpiDetailOpen} onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }} indicator={kpiDetailRow} />

      {/* KPI Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("productKpi.edit.title") : t("productKpi.create.title")}</DialogTitle>
        <DialogContent>
          <KpiConstraintAlert variant={kpiEditMode ? (usedKpiNames.has(kpiForm.indicatorName) ? "edit-locked" : "edit-unused") : "create"} sx={{ mt: 1 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("productKpi.indicatorName")} value={kpiForm.indicatorName} onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField fullWidth required multiline label={t("productKpi.unit")} value={kpiForm.unit} onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField fullWidth multiline label={t("productKpi.measuringMethod")} value={kpiForm.measuringMethod} onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField fullWidth multiline label={t("productKpi.evaluationStandard")} value={kpiForm.evaluationStandard} onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField fullWidth required multiline label={t("productKpi.description")} value={kpiForm.description} onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave} disabled={kpiEditMode && usedKpiNames.has(kpiForm.indicatorName)}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteIndex(-1); }} />

      {/* Criteria Add Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("productCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("productCriteria.code")} value={criteriaForm.classificationCriteriaCd} onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("productCriteria.name")} value={criteriaForm.classificationCriteriaName} onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete} onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />

      {/* Criteria Value Add Dialog */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")}
              value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")}
              value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete} onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />

      {/* Product Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{taskEditMode ? t("productTask.edit.title") : t("productTask.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("productTask.name")} value={taskForm.productTaskName} onChange={(e) => setTaskForm({ ...taskForm, productTaskName: e.target.value })} />
            <TextField fullWidth required multiline label={t("productTask.description")} value={taskForm.productTaskDescription} onChange={(e) => setTaskForm({ ...taskForm, productTaskDescription: e.target.value })} />
            <TextField fullWidth multiline label={t("productTask.url")} value={taskForm.url} onChange={(e) => setTaskForm({ ...taskForm, url: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleTaskSave}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={taskConfirmOpen} onConfirm={handleTaskDelete} onCancel={() => { setTaskConfirmOpen(false); setTaskDeleteTarget(null); }} />
    </Box>
  );
}
